import { Button } from "components/DesignSystem/Button/Button";
import { CategoryMultiSelect } from "components/DesignSystem/CategoryMultiSelect/CategoryMultiSelect";
import Modal from "components/DesignSystem/Modal/Modal";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { useState } from "react";

const HIDDEN_CATEGORY_TYPES = ["BANK_TRANSFER", "PAY_DOWN_CREDIT"] as const;

type ExportFluxAnalysisModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (categoryUuids: string[]) => void;
  isSubmitting?: boolean;
  period: string;
};

type ToggleRowProps = {
  index: number;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  children?: React.ReactNode;
};

const ToggleRow = ({
  index,
  title,
  description,
  checked,
  disabled = false,
  onCheckedChange,
  children,
}: ToggleRowProps) => (
  <div className="t-flex t-gap-3">
    <span className="t-text-body-sm t-text-text-30">{index}</span>
    <div className="t-flex t-w-full t-flex-col t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4 sm:t-px-3">
      <div className="t-flex t-items-start t-justify-between t-gap-5">
        <div className="t-flex t-min-w-0 t-flex-col t-gap-0.5">
          <span className="t-text-subtext t-text-text-100">{title}</span>
          <span className="t-text-body-sm t-italic t-text-text-30">
            {description}
          </span>
        </div>
        <span className="t-shrink-0">
          <Switch
            checked={checked}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
          />
        </span>
      </div>
      {children}
    </div>
  </div>
);

export const ExportFluxAnalysisModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  period,
}: ExportFluxAnalysisModalProps) => {
  const [isVendorBreakdownEnabled, setIsVendorBreakdownEnabled] =
    useState(false);
  const [selectedCategoryUuids, setSelectedCategoryUuids] = useState<string[]>(
    []
  );

  const handleVendorBreakdownToggle = (checked: boolean) => {
    setIsVendorBreakdownEnabled(checked);
    if (!checked) setSelectedCategoryUuids([]);
  };

  const handleConfirm = () =>
    onConfirm(isVendorBreakdownEnabled ? selectedCategoryUuids : []);

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <div>
            <Modal.Title>Export Flux Analysis</Modal.Title>
          </div>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-flex t-flex-col t-gap-2 t-text-body t-text-text-60">
              <div className="t-text-body t-text-text-60">
                Choose what to include.
              </div>
              <span className="t-text-body-sm t-text-text-60">
                Period: {period}
              </span>
            </div>

            <ToggleRow
              index={1}
              title="Flux Summary"
              description="All the categories are included"
              checked
              disabled
            />

            <ToggleRow
              index={2}
              title="Income Statement"
              description="All the categories are included"
              checked
              disabled
            />

            <ToggleRow
              index={3}
              title="Vendor-Breakdown"
              description="Adds a tab to your export. The categories you select below appear on it with a detailed vendor breakdown."
              checked={isVendorBreakdownEnabled}
              onCheckedChange={handleVendorBreakdownToggle}
            >
              {isVendorBreakdownEnabled && (
                <div className="t-flex t-flex-col t-gap-2">
                  <Label className="!t-pb-0" required>
                    Categories to include
                  </Label>
                  <CategoryMultiSelect
                    selectedUuids={selectedCategoryUuids}
                    onChange={setSelectedCategoryUuids}
                    hiddenCategoryTypes={[...HIDDEN_CATEGORY_TYPES]}
                  />
                </div>
              )}
            </ToggleRow>
          </div>
        </Modal.Body>
        <Modal.Footer className="t-flex t-justify-end t-gap-2">
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            customType="primary"
            size="small"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleConfirm}
          >
            Export
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
